import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./WalletManagement.css";

const API_URL = process.env.REACT_APP_API_URL || "";

function WalletManagement() {
  const [totalBalance, setTotalBalance] = useState(0);
  const [balanceError, setBalanceError] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserList, setShowUserList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const searchTimer = useRef(null);
  const adminToken = localStorage.getItem("adminToken");
  const auth = { headers: { Authorization: `Bearer ${adminToken}` } };

  useEffect(() => {
    fetchTotalBalance();
    fetchUsers("");
  }, []);

  const fetchUsers = async (q) => {
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (q) params.set("search", q);
      const response = await axios.get(`${API_URL}/admin/users?${params}`, auth);
      setUsers(response.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const onSearchChange = (value) => {
    setUserSearch(value);
    setShowUserList(true);
    setSelected(null);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchUsers(value.trim()), 300);
  };

  const refreshSelected = async (telegramId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users?search=${telegramId}&limit=5`, auth);
      const fresh = response.data.find((u) => String(u.telegram_id) === String(telegramId));
      if (fresh) setSelected(fresh);
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  };

  const selectUser = (u) => {
    setSelected(u);
    setUserSearch(`${u.username || "Unknown"} (${u.telegram_id})`);
    setShowUserList(false);
    setMessage("");
  };

  const fetchTotalBalance = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/wallet/total-balance`, auth);
      setTotalBalance(response.data.total_balance);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setBalanceError(detail);
      else if (Array.isArray(detail)) setBalanceError(detail.map((e) => e.msg || JSON.stringify(e)).join("; "));
      else setBalanceError("Failed to fetch total balance");
    } finally {
      setLoading(false);
    }
  };

  const currentBalance = selected ? parseFloat(selected.wallet_balance || 0) : 0;
  const amt = parseFloat(amount);
  const validAmount = !isNaN(amt) && amt > 0;
  const newBalance = validAmount ? (mode === "add" ? currentBalance + amt : currentBalance - amt) : currentBalance;
  const overDeduct = mode === "deduct" && validAmount && amt > currentBalance + 1e-9;

  const showMessage = (text, error) => {
    setMessage(text);
    setIsError(error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    if (!selected) return showMessage("Select a player first.", true);
    if (!validAmount) return showMessage("Enter an amount greater than 0.", true);
    if (overDeduct) return showMessage(`You can deduct at most ${currentBalance.toFixed(2)} Birr.`, true);
    setConfirming(true);
  };

  const doAdjust = async () => {
    setConfirming(false);
    setSubmitting(true);
    try {
      const label = mode === "add" ? "Admin add" : "Admin deduct";
      const response = await axios.post(
        `${API_URL}/admin/wallet/adjust`,
        {
          telegram_id: parseInt(selected.telegram_id),
          amount: mode === "add" ? amt : -amt,
          description: reason.trim() ? `${label}: ${reason.trim()}` : label,
        },
        auth,
      );
      showMessage(
        `${mode === "add" ? "Added" : "Deducted"} ${amt.toFixed(2)} Birr. New balance: ${response.data.new_balance.toFixed(2)} Birr`,
        false,
      );
      setAmount("");
      setReason("");
      fetchTotalBalance();
      refreshSelected(selected.telegram_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") showMessage(detail, true);
      else if (Array.isArray(detail)) showMessage(detail.map((e) => e.msg || JSON.stringify(e)).join("; "), true);
      else showMessage("Failed to adjust balance", true);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="wallet-management">
      <header className="page-header">
        <h1>Wallet Management</h1>
        <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
      </header>

      <div className="wallet-stats">
        <div className="stat-card">
          <h3>Total Platform Balance</h3>
          <p className="stat-value">{totalBalance.toFixed(2)} Birr</p>
          {balanceError && (
            <p style={{ color: "#dc2626", fontSize: "0.75rem", marginTop: "0.5rem" }}>⚠️ {balanceError}</p>
          )}
        </div>
      </div>

      <div className="adjust-form-container">
        <h2>Add or Deduct Player Balance</h2>
        {message && <div className={`message ${isError ? "error" : "success"}`}>{message}</div>}

        <form onSubmit={handleSubmit} className="adjust-form">
          <div className="form-group" style={{ position: "relative" }}>
            <label>Player</label>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setShowUserList(true)}
              placeholder="Search by name, Telegram ID, or phone..."
            />
            {showUserList && users.length > 0 && (
              <div className="user-dropdown">
                {users.map((u) => (
                  <div key={u.id} className="user-option" onClick={() => selectUser(u)}>
                    <div>
                      <strong>{u.username || "Unknown"}</strong>
                      <span className="user-meta">TG: {u.telegram_id}</span>
                      {u.phone && <span className="user-meta">📱 {u.phone}</span>}
                    </div>
                    <span className="user-balance">{parseFloat(u.wallet_balance || 0).toFixed(2)} Birr</span>
                  </div>
                ))}
              </div>
            )}
            {showUserList && <div className="dropdown-backdrop" onClick={() => setShowUserList(false)} />}
          </div>

          {selected && (
            <div className="selected-card">
              <div>
                <strong>{selected.username || "Unknown"}</strong>
                <span className="user-meta">TG: {selected.telegram_id}</span>
              </div>
              <div className="selected-balance">
                Current balance: <strong>{currentBalance.toFixed(2)} Birr</strong>
              </div>
            </div>
          )}

          <div className="mode-toggle">
            <button type="button" className={`mode-btn add ${mode === "add" ? "active" : ""}`} onClick={() => setMode("add")}>
              + Add
            </button>
            <button type="button" className={`mode-btn deduct ${mode === "deduct" ? "active" : ""}`} onClick={() => setMode("deduct")}>
              − Deduct
            </button>
          </div>

          <div className="form-group">
            <label>Amount (Birr)</label>
            <div className="amount-row">
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                required
              />
              {mode === "deduct" && selected && (
                <button type="button" className="max-btn" onClick={() => setAmount(currentBalance.toFixed(2))}>
                  Max (to 0)
                </button>
              )}
            </div>
            {selected && validAmount && (
              <small className={overDeduct ? "hint error" : "hint"}>
                {overDeduct
                  ? `Too much: you can deduct at most ${currentBalance.toFixed(2)} Birr.`
                  : `New balance will be ${newBalance.toFixed(2)} Birr`}
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Reason (optional)</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Bonus, correction, refund..." maxLength={120} />
          </div>

          <button
            type="submit"
            className={`adjust-button ${mode === "deduct" ? "deduct" : ""}`}
            disabled={submitting || !selected || !validAmount || overDeduct}
          >
            {submitting ? "Saving..." : mode === "add" ? "Add to Wallet" : "Deduct from Wallet"}
          </button>
        </form>
      </div>

      {confirming && selected && (
        <div className="confirm-overlay" onClick={() => setConfirming(false)}>
          <div className="confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 className={mode === "add" ? "confirm-title add" : "confirm-title deduct"}>
              {mode === "add" ? "Add to wallet?" : "Deduct from wallet?"}
            </h3>
            <div className="confirm-row"><span>Player</span><strong>{selected.username || "Unknown"} ({selected.telegram_id})</strong></div>
            <div className="confirm-row"><span>Amount</span><strong>{mode === "add" ? "+" : "−"}{amt.toFixed(2)} Birr</strong></div>
            <div className="confirm-row"><span>Balance</span><strong>{currentBalance.toFixed(2)} → {newBalance.toFixed(2)} Birr</strong></div>
            {reason.trim() && <div className="confirm-row"><span>Reason</span><strong>{reason.trim()}</strong></div>}
            <div className="confirm-actions">
              <button type="button" className="confirm-cancel" onClick={() => setConfirming(false)}>Cancel</button>
              <button type="button" className={`confirm-ok ${mode}`} onClick={doAdjust} disabled={submitting}>
                {mode === "add" ? "Yes, add" : "Yes, deduct"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletManagement;
